import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { products } from "@/mocks/products";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const faqItems = [
  { q: "faq1Q", a: "faq1A" },
  { q: "faq2Q", a: "faq2A" },
  { q: "faq3Q", a: "faq3A" },
  { q: "faq4Q", a: "faq4A" },
  { q: "faq5Q", a: "faq5A" },
];

export default function Home() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [cookieVisible, setCookieVisible] = useState(true);

  useEffect(() => {
    const accepted = localStorage.getItem("cookieAccepted");
    if (accepted) setCookieVisible(false);
  }, []);

  const acceptCookies = () => {
    localStorage.setItem("cookieAccepted", "true");
    setCookieVisible(false);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20 md:pt-24 pb-16 md:pb-24 overflow-hidden">
        {/* Background with product images */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-background-50/95 via-background-50/85 to-background-100/90 z-10" />
          <div className="grid grid-cols-4 gap-1 opacity-20 h-full">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="relative overflow-hidden h-full">
                <img
                  src={categories[i % categories.length]?.image}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center pt-12 md:pt-20">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-tight mb-6">
              {t("heroTitle")}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              {t("heroSubtitle")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm md:text-base hover:bg-primary-600 transition-colors shadow-sm whitespace-nowrap"
              >
                {t("heroCtaPrimary")}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-lg border-2 border-primary-500 text-primary-700 font-semibold text-sm md:text-base hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                {t("heroCtaSecondary")}
              </a>
            </div>
          </div>
        </div>

        {/* Trust Bar */}
        <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 mt-16 md:mt-20">
          <div className="max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {[
              { icon: "ri-shield-check-line", text: "trustIndependent" },
              { icon: "ri-refresh-line", text: "trustUpdated" },
              { icon: "ri-global-line", text: "trustLanguages" },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2 text-sm text-foreground-600">
                <i className={`${item.icon} text-primary-500`} />
                <span>{t(item.text)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Is the AllChinaBuy Spreadsheet */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold uppercase tracking-wider mb-4">
                  {t("spreadsheetHub")}
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {t("sectionWhatIs")}
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-6">
                  {t("sectionWhatIsDesc")}
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
                  >
                    {t("ctaMain")}
                  </a>
                  <Link
                    to="/guides"
                    className="px-4 py-2 rounded-lg border border-background-300 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors"
                  >
                    {t("ctaGuide")}
                  </Link>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden bg-background-200/50 border border-background-200/70 p-6">
                <div className="grid grid-cols-2 gap-3">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="rounded-xl overflow-hidden aspect-square">
                      <img
                        src={categories[i]?.image}
                        alt={t(categories[i]?.nameKey)}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Categories */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {t("sectionCategories")}
              </h2>
              <p className="text-foreground-600 max-w-xl mx-auto">
                Browse our curated product categories and find the best items on AllChinaBuy spreadsheet.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group block rounded-xl overflow-hidden border border-background-200/70 bg-background-100 hover:border-primary-300 hover:shadow-md transition-all"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={cat.image}
                      alt={t(cat.nameKey)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3">
                    <p className="font-semibold text-sm text-foreground-800 group-hover:text-primary-700 transition-colors">
                      {t(cat.nameKey)}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {t("sectionHowItWorks")}
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { step: "1", icon: "ri-search-2-line", text: "sectionHowItWorksStep1" },
                { step: "2", icon: "ri-bar-chart-grouped-line", text: "sectionHowItWorksStep2" },
                { step: "3", icon: "ri-book-open-line", text: "sectionHowItWorksStep3" },
                { step: "4", icon: "ri-shopping-cart-line", text: "sectionHowItWorksStep4" },
              ].map((item) => (
                <div key={item.step} className="relative p-6 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-primary-600`} />
                  </div>
                  <span className="absolute top-4 right-4 font-heading text-3xl font-bold text-background-200">
                    {item.step}
                  </span>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {t(item.text)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Review / Shipping / Warehouse / Safety Teasers */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "sectionReview",
                  desc: "sectionReviewDesc",
                  icon: "ri-star-line",
                  color: "accent",
                  link: "/reviews",
                },
                {
                  title: "sectionShipping",
                  desc: "sectionShippingDesc",
                  icon: "ri-truck-line",
                  color: "primary",
                  link: "/shipping",
                },
                {
                  title: "sectionWarehouse",
                  desc: "sectionWarehouseDesc",
                  icon: "ri-building-2-line",
                  color: "secondary",
                  link: "/guides",
                },
                {
                  title: "sectionSafety",
                  desc: "sectionSafetyDesc",
                  icon: "ri-shield-check-line",
                  color: "accent",
                  link: "/safety",
                },
              ].map((card) => (
                <Link
                  key={card.title}
                  to={card.link}
                  className="group p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-300 hover:shadow-sm transition-all"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    card.color === "primary" ? "bg-primary-100" :
                    card.color === "accent" ? "bg-accent-100" : "bg-secondary-100"
                  }`}>
                    <i className={`${card.icon} text-lg ${
                      card.color === "primary" ? "text-primary-600" :
                      card.color === "accent" ? "text-accent-600" : "text-secondary-600"
                    }`} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors">
                    {t(card.title)}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {t(card.desc)}
                  </p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all">
                    {t("learnMore")} <i className="ri-arrow-right-line" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  Featured Products
                </h2>
                <p className="text-foreground-600 text-sm">
                  Handpicked items from the AllChinaBuy spreadsheet
                </p>
              </div>
              <Link
                to="/products"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                View All <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {products.slice(0, 8).map((product) => (
                <div
                  key={product.id}
                  className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden hover:border-primary-300 hover:shadow-sm transition-all flex flex-col"
                >
                  <div className="relative aspect-square overflow-hidden bg-background-200/50">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3 flex flex-col flex-1">
                    <h3 className="text-xs font-semibold text-foreground-900 leading-snug mb-1.5 line-clamp-2 flex-1">
                      {product.name}
                    </h3>
                    <p className="text-xs font-bold text-primary-600 mb-2">
                      {product.price}
                    </p>
                    <a
                      href={product.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block w-full text-center px-2 py-1.5 rounded-lg bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      {t("buyNow")}
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/products"
                className="inline-flex items-center gap-1 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
              >
                View All Products <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {t("sectionArticles")}
              </h2>
              <Link
                to="/guides"
                className="text-sm font-semibold text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                {t("learnMore")} <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group block rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-300 hover:shadow-sm transition-all"
                >
                  <div className="aspect-video bg-gradient-to-br from-primary-100/50 to-accent-100/50 flex items-center justify-center">
                    <i className="ri-article-line text-4xl text-primary-400/50" />
                  </div>
                  <div className="p-5">
                    <span className="inline-block px-2 py-0.5 rounded-md bg-background-200/70 text-foreground-500 text-xs font-medium uppercase tracking-wider mb-3">
                      {article.category}
                    </span>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors line-clamp-2">
                      {t(article.titleKey)}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3">
                      {t(article.descKey)}
                    </p>
                    <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span className="flex items-center gap-1 text-primary-600 font-medium">
                        {t("readMore")} <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {t("sectionFaq")}
              </h2>
            </div>
            <div className="space-y-3">
              {faqItems.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-background-200/70 bg-background-100 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left"
                  >
                    <span className="font-semibold text-sm md:text-base text-foreground-900 pr-4">
                      {t(faq.q)}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-400 transition-transform shrink-0 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed border-t border-background-200/70 pt-4">
                      {t(faq.a)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-primary-500/10">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              {t("finalCtaTitle")}
            </h2>
            <p className="text-foreground-600 mb-8 max-w-xl mx-auto">
              {t("finalCtaDesc")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors shadow-sm whitespace-nowrap"
              >
                {t("heroCtaPrimary")}
              </a>
              <Link
                to="/guides"
                className="px-6 py-3 rounded-lg border-2 border-primary-500 text-primary-700 font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                {t("heroCtaSecondary")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Cookie Notice */}
      {cookieVisible && (
        <div className="fixed bottom-0 left-0 right-0 bg-background-50/95 backdrop-blur-md border-t border-background-200/70 p-4 z-50">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">{t("cookieNotice")}</p>
            <button
              onClick={acceptCookies}
              className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t("cookieAccept")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}